import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Table, Form, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getOrder, updateOrder, deleteOrder, updateDeliveryStatus, reprocessOrder, getDeliveryStatusHistory } from '../../../services/OrdersService';
import Swal from 'sweetalert2';
import { getProducts } from '../../../services/ProductsService';
import OrderInvoice from './OrderInvoice';
import ShippingLabel from './ShippingLabel';
import { DELIVERY_STATUS_OPTIONS, DELIVERY_STATUS_EMAIL_ELIGIBLE } from './deliveryStatusConstants';
import DeliveryStatusEmailModal from './DeliveryStatusEmailModal';
import DeliveryWindowSelect from './DeliveryWindowSelect';

function formatDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleString();
}

export default function OrderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(() => location.state?.startEditing === true);
    const [viewInvoiceId, setViewInvoiceId] = useState(null); // null = the regular invoice (or concept)
    const [products, setProducts] = useState([]);
    const [editNotes, setEditNotes] = useState('');
    const [editBuyerName, setEditBuyerName] = useState('');
    const [editOrderDate, setEditOrderDate] = useState('');
    const [editDeliveryDate, setEditDeliveryDate] = useState('');
    const [editExpectedDate, setEditExpectedDate] = useState('');
    const [editDeliveryWindow, setEditDeliveryWindow] = useState('');
    const [editStreetName, setEditStreetName] = useState('');
    const [editHouseNumber, setEditHouseNumber] = useState('');
    const [editZipCode, setEditZipCode] = useState('');
    const [editCustomerEmail, setEditCustomerEmail] = useState('');
    const [editCustomerPhone, setEditCustomerPhone] = useState('');
    const [deliveryModal, setDeliveryModal] = useState(null); // { targetStatus } | null
    const [deliverySubmitting, setDeliverySubmitting] = useState(false);
    const [editPaymentStatus, setEditPaymentStatus] = useState(''); // 'paid' | 'unpaid' | 'partial'
    const [editAmountPaidPartial, setEditAmountPaidPartial] = useState(''); // for partial payments
    const [editItems, setEditItems] = useState([]); // { product_id, product_name, quantity }
    const [addingProductId, setAddingProductId] = useState('');
    const [addingQty, setAddingQty] = useState('1');
    const [addingDiscount, setAddingDiscount] = useState(0);
    const [addingNote, setAddingNote] = useState('');
    const [emailWarnings, setEmailWarnings] = useState([]); // status emails that went out without the invoice PDF (or failed)

    const load = async () => {
        try {
            setLoading(true);
            const res = await getOrder(id);
            setOrder(res.data);
        } catch {
            toast.error(t('orderDetail.errors.loadOrder'));
            navigate('/orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    // Automatic status emails record a problem on the delivery-status history row (e.g. the invoice PDF could not be made and
    // the email was sent without it). Show it here so it is not silent. Best effort: a failing lookup shows nothing.
    useEffect(() => {
        if (!order?.id) return undefined;
        let cancelled = false;
        Promise.resolve()
            .then(() => getDeliveryStatusHistory(order.id))
            .then((res) => { if (!cancelled) setEmailWarnings((res && Array.isArray(res.data) ? res.data : []).filter((h) => h.email_error)); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [order?.id]);

    useEffect(() => {
        if (editing && order) {
            setEditNotes(order.notes || '');
            setEditBuyerName(order.buyer_name || '');
            setEditOrderDate(order.order_date ? order.order_date.slice(0, 10) : new Date().toISOString().slice(0, 10));
            setEditDeliveryDate(order.delivery_date ? order.delivery_date.slice(0, 10) : '');
            setEditExpectedDate(order.expected_delivery_date ? order.expected_delivery_date.slice(0, 10) : '');
            setEditDeliveryWindow(order.delivery_window || '');
            setEditStreetName(order.street_name || '');
            setEditHouseNumber(order.house_number || '');
            setEditZipCode(order.zip_code || '');
            setEditCustomerEmail(order.customer_email || '');
            setEditCustomerPhone(order.customer_phone || '');
            // Set payment status from order
            const ps = order.payment_status || (order.is_paid === true ? 'paid' : order.is_paid === false ? 'unpaid' : 'unpaid');
            setEditPaymentStatus(ps);
            setEditAmountPaidPartial(order.amount_paid ? String(order.amount_paid) : '');
            setEditItems((order.items || []).map(it => ({
                product_id: it.product_id,
                product_name: it.product_name_nl || it.product_name,
                product_name_nl: it.product_name_nl || it.product_name,
                product_name_en: it.product_name_en || '',
                quantity: it.quantity,
                discount_percent: Math.min(100, Math.max(0, Number(it.discount_percent) || 0)),
                notes: it.notes || '',
            })));
            getProducts().then(res => setProducts(res.data || [])).catch(() => toast.error(t('createOrder.errors.loadProducts')));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing, order]);

    const applyDeliveryStatus = async (newStatus, sendEmail, expectedDeliveryDate, deliveryWindow) => {
        setDeliverySubmitting(true);
        try {
            // The invoice PDF attached to a manual status email is made by the SERVER (print layout): nothing is uploaded from the browser.
            const payload = { delivery_status: newStatus, send_email: sendEmail };
            if (expectedDeliveryDate) payload.expected_delivery_date = expectedDeliveryDate;
            if (expectedDeliveryDate && deliveryWindow) payload.delivery_window = deliveryWindow;
            const res = await updateDeliveryStatus(order.id, payload);
            const emailOutcome = res.data?.email;
            if (emailOutcome?.attempted && emailOutcome.sent === false) {
                toast.warning(t('tasksPage.toasts.deliveryStatusUpdatedEmailFailed', { error: emailOutcome.error || '' }));
            } else if (emailOutcome?.attempted && emailOutcome.sent === true) {
                toast.success(t('tasksPage.toasts.deliveryStatusUpdatedEmailSent'));
            } else {
                toast.success(t('tasksPage.toasts.deliveryStatusUpdated'));
            }
            setOrder(res.data.order);
            setDeliveryModal(null);
        } catch (err) {
            const msg = err.response?.data?.error || t('tasksPage.errors.updateDeliveryStatus');
            toast.error(msg);
        } finally {
            setDeliverySubmitting(false);
        }
    };

    const handleDeliveryStatusChange = (newStatus) => {
        if (DELIVERY_STATUS_EMAIL_ELIGIBLE.includes(newStatus)) {
            setDeliveryModal({ targetStatus: newStatus });
        } else {
            applyDeliveryStatus(newStatus, false);
        }
    };

    const addEditItem = () => {
        const pid = parseInt(addingProductId);
        const qty = parseFloat(addingQty);
        if (!pid || qty <= 0) {
            toast.warning(t('createOrder.errors.selectProductAndQty'));
            return;
        }
        const prod = products.find(p => p.id === pid);
        const existing = editItems.find(i => i.product_id === pid);
        if (existing) {
            setEditItems(editItems.map(i => i.product_id === pid ? { ...i, quantity: i.quantity + qty } : i));
        } else {
            setEditItems([...editItems, { product_id: pid, product_name: prod?.name_nl || prod?.name || '', product_name_nl: prod?.name_nl || prod?.name || '', product_name_en: prod?.name_en || '', quantity: qty, discount_percent: addingDiscount, notes: addingNote.trim() || '' }]);
        }
        setAddingProductId('');
        setAddingQty('1');
        setAddingDiscount(0);
        setAddingNote('');
    };

    const removeEditItem = (productId) => setEditItems(editItems.filter(i => i.product_id !== productId));

    const updateEditItemQty = (productId, qty) => {
        const val = parseFloat(qty);
        setEditItems(editItems.map(i => i.product_id === productId ? { ...i, quantity: isNaN(val) || val <= 0 ? 1 : val } : i));
    };

    // Expected delivery date + time window are editable while the order is (or was set) ready / out for delivery
    const showExpectedFields = !!order && (['ready_to_deliver', 'out_for_delivery'].includes(order.delivery_status) || !!order.expected_delivery_date);

    const getEditTotal = () => editItems.reduce((sum, it) => {
        const prod = products.find(p => p.id === it.product_id);
        const price = prod?.price_incl_tax ?? prod?.price ?? 0;
        const discount = Math.min(100, Math.max(0, Number(it.discount_percent) || 0));
        return sum + it.quantity * price * (100 - discount) / 100;
    }, 0);

    const getProductDisplayName = useCallback((nameNl, nameEn) => {
        const nl = nameNl || '';
        const en = nameEn || '';
        return (i18n.language.startsWith('en') && en) ? en : (nl || en);
    }, [i18n.language]);

    const handleSaveEdit = async () => {
        const validItems = editItems.filter(i => i.quantity > 0);
        if (validItems.length === 0) {
            toast.warning(t('orderDetail.errors.atLeastOneItem'));
            return;
        }
        if (!editOrderDate?.trim()) {
            toast.warning(t('createOrder.errors.orderDateRequired'));
            return;
        }
        if (!editDeliveryDate?.trim()) {
            toast.warning(t('createOrder.errors.deliveryDateRequired'));
            return;
        }
        if (editExpectedDate && !editDeliveryWindow) {
            toast.warning(t('deliveryWindows.required'));
            return;
        }
        if (!['paid', 'unpaid', 'partial'].includes(editPaymentStatus)) {
            toast.warning(t('orderDetail.errors.selectPaymentStatus'));
            return;
        }
        const total = getEditTotal();
        if (editPaymentStatus === 'partial') {
            const paid = parseFloat(editAmountPaidPartial);
            if (isNaN(paid) || paid < 0 || paid > total) {
                toast.warning(t('createOrder.errors.amountPaidRange', { total: total.toFixed(2) }));
                return;
            }
        }
        try {
            const payload = {
                order_date: editOrderDate.trim(),
                delivery_date: editDeliveryDate.trim(),
                buyer_name: editBuyerName.trim() || null,
                payment_status: editPaymentStatus,
                notes: editNotes.trim() || null,
                customer_email: editCustomerEmail.trim() || null,
                customer_phone: editCustomerPhone.trim() || null,
                street_name: editStreetName.trim() || null,
                house_number: editHouseNumber.trim() || null,
                zip_code: editZipCode.trim() || null,
                items: validItems.map(i => ({
                    product_id: i.product_id,
                    quantity: i.quantity,
                    discount_percent: Math.min(100, Math.max(0, Number(i.discount_percent) || 0)),
                    notes: i.notes?.trim() || null,
                })),
            };
            if (editPaymentStatus === 'partial') {
                payload.amount_paid = parseFloat(editAmountPaidPartial);
            }
            // The expected delivery date + time window (the tracking site follows at once; no email is sent from an edit)
            if (showExpectedFields) {
                payload.expected_delivery_date = editExpectedDate || '';
                if (editExpectedDate) payload.delivery_window = editDeliveryWindow;
            }
            const res = await updateOrder(order.id, payload);
            toast.success(t('orderDetail.orderUpdated'));
            setOrder(res.data);
            setEditing(false);
        } catch (err) {
            const data = err.response?.data;
            const msg = (data?.error_key && t(data.error_key)) || data?.error || err.message || t('orderDetail.errors.updateOrder');
            const details = data?.details;
            toast.error(details ? `${msg}: ${details}` : msg);
        }
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setAddingProductId('');
        setAddingQty('1');
        setAddingDiscount(0);
        setAddingNote('');
        setEditPaymentStatus('');
        setEditAmountPaidPartial('');
    };

    const handleCancelOrder = async () => {
        const result = await Swal.fire({
            title: t('tasksPage.dialogs.cancelTitle'),
            text: t('tasksPage.dialogs.cancelText', { number: order.order_number }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('tasksPage.dialogs.cancelConfirm'),
        });
        if (result.isConfirmed) {
            try {
                const res = await updateDeliveryStatus(order.id, { delivery_status: 'cancelled', send_email: false });
                toast.success(t('tasksPage.toasts.orderCancelled'));
                setOrder(res.data.order);
            } catch {
                toast.error(t('tasksPage.errors.cancelOrder'));
            }
        }
    };

    const handleDeleteOrder = async () => {
        const result = await Swal.fire({
            title: t('tasksPage.dialogs.deleteTitle'),
            text: t('tasksPage.dialogs.deleteText', { number: order.order_number }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('tasksPage.dialogs.deleteConfirm'),
        });
        if (result.isConfirmed) {
            try {
                await deleteOrder(order.id);
                toast.success(t('orderDetail.orderDeleted'));
                navigate('/orders');
            } catch (err) {
                const errorKey = err.response?.data?.error_key;
                toast.error((errorKey && t(errorKey)) || t('tasksPage.errors.deleteOrder'));
            }
        }
    };

    const handleReprocess = async () => {
        try {
            const res = await reprocessOrder(order.id);
            toast.success(t('orders.reprocessed'));
            const refreshed = await getOrder(order.id);
            setOrder(refreshed.data);
            return res;
        } catch (err) {
            const errorKey = err.response?.data?.error_key;
            toast.error((errorKey && t(errorKey)) || t('orders.reprocessFailed'));
            return null;
        }
    };

    if (loading || !order) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card className="order-detail-card">
                        <Card.Header className="d-flex justify-content-between align-items-center order-detail-header">
                            <div>
                                <h4 className="heading mb-0">{t('orderDetail.heading', { number: order.order_number })}</h4>
                                <span className="text-muted order-detail-meta">
                                    {t('orderDetail.metaOrder', { date: order.order_date ? new Date(order.order_date).toLocaleDateString() : '-' })}
                                    {order.delivery_date && t('orderDetail.metaDelivery', { date: new Date(order.delivery_date).toLocaleDateString() })}
                                    {order.expected_delivery_date && (
                                        <span data-testid="expected-delivery-meta">
                                            {' | '}{t('deliveryWindows.metaExpected', { date: new Date(order.expected_delivery_date).toLocaleDateString() })}
                                            {order.delivery_window && `, ${t(`deliveryWindows.names.${order.delivery_window}`)}`}
                                        </span>
                                    )}
                                    {order.buyer_name && t('orderDetail.metaBuyer', { name: order.buyer_name })}
                                    {order.payment_method && t('orderDetail.metaPayment', { method: order.payment_method })}
                                    {t('orderDetail.metaCreated', { date: formatDate(order.created_at) })}
                                </span>
                                <div className="mt-1 order-detail-flags">
                                    {order.source && order.source !== 'manual' && (
                                        <span className="badge order-flag-badge order-flag-badge--source ms-0">
                                            {t(`orders.flags.source.${order.source}`)}{order.external_name ? ` ${order.external_name}` : ''}
                                        </span>
                                    )}
                                    {order.is_test && <span className="badge order-flag-badge order-flag-badge--test">{t('orders.flags.test')}</span>}
                                    {order.is_marketing && <span className="badge order-flag-badge order-flag-badge--marketing" data-testid="marketing-badge" title={t('orders.marketing.notPushed')}>{t('orders.flags.marketing')}</span>}
                                    {order.prices_as_sent && <span className="badge order-flag-badge order-flag-badge--asSent">{t('orders.flags.asSent')}</span>}
                                    {order.needs_review && <span className="badge order-flag-badge order-flag-badge--review">{order.deleted_in_shopify ? t('orders.flags.deletedInShopify') : t('orders.flags.needsReview')}</span>}
                                    {order.invoice_state === 'concept' && <span className="badge order-flag-badge order-flag-badge--concept">{t('orders.flags.concept')}</span>}
                                </div>
                                {order.invoice_status && order.invoice_status.state !== 'issued' && (
                                    <div className="small mt-1" data-testid="invoice-status">
                                        <span className="fw-semibold">{t('orders.invoiceStatus.label')}:</span>{' '}
                                        {t(`orders.invoiceStatus.${order.invoice_status.state}`)}
                                        {order.invoice_status.reason_key && ['blocked', 'needs_review'].includes(order.invoice_status.state) && (
                                            <span className="text-danger"> ({t(order.invoice_status.reason_key, { fields: '', defaultValue: order.invoice_status.message })})</span>
                                        )}
                                    </div>
                                )}
                                {emailWarnings.map((w) => (
                                    <div className="small text-danger mt-1" key={w.id} data-testid="email-warning">
                                        {String(w.email_error).startsWith('invoice_pdf_failed')
                                            ? t('orders.emailWarning.pdfFailed', { status: t(`deliveryStatuses.${w.new_status}`) })
                                            : t('orders.emailWarning.sendFailed', { status: t(`deliveryStatuses.${w.new_status}`) })}
                                    </div>
                                ))}
                                {order.needs_review && order.review_reason && (
                                    <div className="small text-danger mt-1">
                                        {t('orders.reviewReason')}: {order.review_reason}
                                        {order.external_id && (
                                            <Button variant="link" size="sm" className="p-0 ms-2" onClick={handleReprocess}>{t('orders.reprocess')}</Button>
                                        )}
                                    </div>
                                )}
                                {order.paid_at && (
                                    <div className="small text-muted mt-1">
                                        {t('orders.paymentInfo.paidAt')}: {formatDate(order.paid_at)}
                                        {order.payment_method_normalized ? ` · ${t('orders.paymentInfo.method')}: ${order.payment_method_normalized}` : ''}
                                        {['refunded', 'partially_refunded'].includes(order.payment_state) && (
                                            <span className="text-danger">
                                                {` · ${t(`orders.paymentInfo.state.${order.payment_state}`)}: ${t('orders.paymentInfo.refundedAmount')} €${Number(order.refunded || 0).toFixed(2)}`}
                                                {order.net_paid != null ? `, ${t('orders.paymentInfo.netPaid')} €${Number(order.net_paid).toFixed(2)}` : ''}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="d-flex gap-2 order-detail-actions">
                                {editing ? (
                                    <>
                                        <Button variant="outline-secondary" size="sm" onClick={handleCancelEdit}>
                                            {t('common.cancel')}
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={handleSaveEdit} disabled={editItems.filter(i => i.quantity > 0).length === 0 || !editOrderDate?.trim() || !editDeliveryDate?.trim()}>
                                            <i className="fa-solid fa-check me-1" /> {t('common.save')}
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => setEditing(true)}
                                        disabled={order.prices_as_sent || (order.source && order.source !== 'manual') || (order.invoices || []).some((inv) => !inv.is_legacy && ['invoice', 'marketing'].includes(inv.kind))}
                                        title={(order.prices_as_sent || (order.source && order.source !== 'manual')) ? t('orders.errors.itemsLockedExternal') : ((order.invoices || []).some((inv) => !inv.is_legacy && ['invoice', 'marketing'].includes(inv.kind)) ? t('orders.errors.itemsLockedInvoiced') : undefined)}
                                    >
                                        <i className="fa-solid fa-pen me-1" /> {t('orderDetail.editButton')}
                                    </Button>
                                )}
                                <Button variant="warning" size="sm" onClick={handleCancelOrder} title={t('orderDetail.cancelOrderTitle')}>
                                    <i className="fa-solid fa-ban me-1" /> {t('common.cancel')}
                                </Button>
                                {order.is_test ? (
                                    <Button variant="danger" size="sm" onClick={handleDeleteOrder} title={t('orderDetail.deleteOrderTitle')}>
                                        <i className="fa-solid fa-trash me-1" /> {t('common.delete')}
                                    </Button>
                                ) : (
                                    <span className="d-inline-block" title={t('orders.deleteRealTip')} data-testid="delete-real-tip">
                                        <Button variant="danger" size="sm" disabled>
                                            <i className="fa-solid fa-trash me-1" /> {t('common.delete')}
                                        </Button>
                                    </span>
                                )}
                                <Button variant="light" size="sm" onClick={() => navigate('/orders')}>
                                    <i className="fa-solid fa-arrow-left me-1" /> {t('createOrder.backButton')}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-4 d-flex gap-4 flex-wrap">
                                <Form.Group className="order-detail-delivery-status">
                                    <Form.Label>{t('tasksPage.columns.deliveryStatus')}</Form.Label>
                                    <Form.Select
                                        value={order.delivery_status || 'preparing'}
                                        onChange={(e) => handleDeliveryStatusChange(e.target.value)}
                                        style={{ maxWidth: 200 }}
                                    >
                                        {DELIVERY_STATUS_OPTIONS.map(s => (
                                            <option key={s.value} value={s.value}>{t(`deliveryStatuses.${s.value}`)}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>

                            {editing ? (
                                <>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.orderDate')} <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={editOrderDate}
                                                    onChange={(e) => setEditOrderDate(e.target.value)}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.deliveryDate')} <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={editDeliveryDate}
                                                    onChange={(e) => setEditDeliveryDate(e.target.value)}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        {showExpectedFields && (
                                            <Col md={8}>
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>{t('orderDetail.deliveryStatusModal.expectedDeliveryDateLabel')}</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={editExpectedDate}
                                                                onChange={(e) => {
                                                                    setEditExpectedDate(e.target.value);
                                                                    if (!e.target.value) setEditDeliveryWindow('');
                                                                }}
                                                                data-testid="edit-expected-date"
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        {editExpectedDate && (
                                                            <DeliveryWindowSelect value={editDeliveryWindow} onChange={setEditDeliveryWindow} idPrefix="edit-delivery-window" />
                                                        )}
                                                    </Col>
                                                </Row>
                                            </Col>
                                        )}
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.buyerName')}</Form.Label>
                                                <Form.Control
                                                    value={editBuyerName}
                                                    onChange={(e) => setEditBuyerName(e.target.value)}
                                                    placeholder={t('createOrder.form.buyerNamePlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.isPaid')} <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    value={editPaymentStatus}
                                                    onChange={(e) => {
                                                        setEditPaymentStatus(e.target.value);
                                                        if (e.target.value !== 'partial') setEditAmountPaidPartial('');
                                                    }}
                                                    required
                                                >
                                                    <option value="">{t('createOrder.form.selectOption')}</option>
                                                    <option value="paid">{t('common.yes')}</option>
                                                    <option value="unpaid">{t('common.no')}</option>
                                                    <option value="partial">{t('tasksPage.paymentPartial')}</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.paymentMethod')}</Form.Label>
                                                <Form.Control
                                                    plaintext
                                                    readOnly
                                                    value={order.payment_method || t('orderDetail.paymentMethodAuto')}
                                                    className="text-muted"
                                                />
                                                <div className="text-muted small">{t('orderDetail.paymentMethodAutoHint')}</div>
                                            </Form.Group>
                                        </Col>
                                        {editPaymentStatus === 'partial' && (
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('createOrder.form.amountPaid')} <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max={getEditTotal()}
                                                        value={editAmountPaidPartial}
                                                        onChange={(e) => setEditAmountPaidPartial(e.target.value)}
                                                        placeholder={`0 - ${getEditTotal().toFixed(2)}`}
                                                        required={editPaymentStatus === 'partial'}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        {t('createOrder.form.remaining', { amount: (getEditTotal() - (parseFloat(editAmountPaidPartial) || 0)).toFixed(2) })}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        )}
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.notes')}</Form.Label>
                                                <Form.Control
                                                    value={editNotes}
                                                    onChange={(e) => setEditNotes(e.target.value)}
                                                    placeholder={t('createOrder.form.notesPlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.customerEmail')}</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    value={editCustomerEmail}
                                                    onChange={(e) => setEditCustomerEmail(e.target.value)}
                                                    placeholder={t('createOrder.form.customerEmailPlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.customerPhone')}</Form.Label>
                                                <Form.Control
                                                    type="tel"
                                                    value={editCustomerPhone}
                                                    onChange={(e) => setEditCustomerPhone(e.target.value)}
                                                    placeholder={t('createOrder.form.customerPhonePlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <h6 className="mb-2 text-muted">{t('createOrder.form.deliveryAddressHeading')}</h6>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.streetName')}</Form.Label>
                                                <Form.Control
                                                    value={editStreetName}
                                                    onChange={(e) => setEditStreetName(e.target.value)}
                                                    placeholder={t('createOrder.form.streetNamePlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.houseNumber')}</Form.Label>
                                                <Form.Control
                                                    value={editHouseNumber}
                                                    onChange={(e) => setEditHouseNumber(e.target.value)}
                                                    placeholder={t('createOrder.form.houseNumberPlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('createOrder.form.zipCode')}</Form.Label>
                                                <Form.Control
                                                    value={editZipCode}
                                                    onChange={(e) => setEditZipCode(e.target.value)}
                                                    placeholder={t('createOrder.form.zipCodePlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <h6 className="mb-2">{t('orderDetail.orderItemsHeading')}</h6>
                                    <Row className="mb-3 align-items-end">
                                        <Col md={4}>
                                            <Form.Select
                                                value={addingProductId}
                                                onChange={(e) => setAddingProductId(e.target.value)}
                                            >
                                                <option value="">{t('orderDetail.selectProductPlaceholder')}</option>
                                                {products.map(p => (
                                                    <option key={p.id} value={p.id}>
                                                        {p.name} ({p.price_incl_tax?.toFixed(2)} each)
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                placeholder={t('createOrder.addItems.qtyPlaceholder')}
                                                value={addingQty}
                                                onChange={(e) => setAddingQty(e.target.value)}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="small">{t('createOrder.addItems.discountLabel')}</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    value={addingDiscount}
                                                    onChange={(e) => setAddingDiscount(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                                    placeholder="0"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-0">
                                                <Form.Label className="small">{t('createOrder.addItems.itemNoteLabel')}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={addingNote}
                                                    onChange={(e) => setAddingNote(e.target.value)}
                                                    placeholder={t('createOrder.addItems.itemNotePlaceholder')}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={1}>
                                            <Button variant="primary" type="button" size="sm" onClick={addEditItem}>
                                                <i className="fa-solid fa-plus me-1" /> {t('createOrder.addItems.addButton')}
                                            </Button>
                                        </Col>
                                    </Row>
                                    <Table responsive bordered className="order-detail-items-table">
                                        <thead>
                                            <tr>
                                                <th>{t('createOrder.table.quantity')}</th>
                                                <th>{t('createOrder.table.product')}</th>
                                                <th>{t('createOrder.table.unitPrice')}</th>
                                                <th>{t('createOrder.table.total')}</th>
                                                <th className="text-center">{t('createOrder.table.discount')}</th>
                                                <th>{t('createOrder.table.note')}</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center text-muted py-4">
                                                        {t('createOrder.table.noItems')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                editItems.map((it) => {
                                                    const prod = products.find(p => p.id === it.product_id);
                                                    const price = prod?.price_incl_tax ?? prod?.price ?? 0;
                                                    return (
                                                        <tr key={it.product_id}>
                                                            <td>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0.01"
                                                                    size="sm"
                                                                    style={{ maxWidth: 100 }}
                                                                    value={it.quantity}
                                                                    onChange={(e) => updateEditItemQty(it.product_id, e.target.value)}
                                                                />
                                                            </td>
                                                            <td>{getProductDisplayName(it.product_name_nl || it.product_name, it.product_name_en)}</td>
                                                            <td>{price.toFixed(2)}</td>
                                                            <td>{(() => {
                                                                const d = Math.min(100, Math.max(0, Number(it.discount_percent) || 0));
                                                                return (it.quantity * price * (100 - d) / 100).toFixed(2);
                                                            })()}</td>
                                                            <td>
                                                                <Form.Control
                                                                    type="number"
                                                                    size="sm"
                                                                    min="0"
                                                                    max="100"
                                                                    step="1"
                                                                    value={Math.min(100, Math.max(0, Number(it.discount_percent) || 0))}
                                                                    onChange={(e) => {
                                                                        const v = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                                                        setEditItems(editItems.map(i => i.product_id === it.product_id ? { ...i, discount_percent: v } : i));
                                                                    }}
                                                                    style={{ maxWidth: 80 }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Form.Control
                                                                    type="text"
                                                                    size="sm"
                                                                    value={it.notes || ''}
                                                                    onChange={(e) => {
                                                                        setEditItems(editItems.map(i => i.product_id === it.product_id ? { ...i, notes: e.target.value } : i));
                                                                    }}
                                                                    placeholder={t('createOrder.table.notePlaceholder')}
                                                                    style={{ minWidth: 150 }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Button variant="light" size="sm" className="text-danger" onClick={() => removeEditItem(it.product_id)}>
                                                                    <i className="fa-solid fa-trash" />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </Table>
                                    <div className="mt-3 pt-3 border-top">
                                        <h5>{t('createOrder.totalLabel')} <span className="text-primary">{getEditTotal().toFixed(2)}</span></h5>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {order.notes && (
                                        <div className="mb-4">
                                            <strong>{t('orderDetail.notesLabel')}</strong> {order.notes}
                                        </div>
                                    )}

                                    {(order.customer_email || order.customer_phone) && (
                                        <div className="mb-4">
                                            <strong>{t('orderDetail.customerContactLabel')}</strong>{' '}
                                            {[order.customer_email, order.customer_phone].filter(Boolean).join(' · ')}
                                        </div>
                                    )}

                                    {(order.street_name || order.house_number || order.zip_code) && (
                                        <div className="mb-4">
                                            <strong>{t('orderDetail.deliveryAddressLabel')}</strong>{' '}
                                            {[order.street_name, order.house_number, order.zip_code].filter(Boolean).join(', ')}
                                        </div>
                                    )}

                                    <Table responsive bordered className="order-detail-items-table order-detail-items-table--view">
                                        <thead>
                                            <tr>
                                                <th>{t('orderDetail.viewColumns.qty')}</th>
                                                <th>{t('createOrder.table.product')}</th>
                                                <th>{t('createOrder.table.unitPrice')}</th>
                                                <th>{t('createOrder.table.total')}</th>
                                                <th className="text-center">{t('createOrder.table.discount')}</th>
                                                <th>{t('createOrder.table.note')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(order.items || []).map((it) => (
                                                <tr key={it.id}>
                                                    <td>{it.quantity}</td>
                                                    <td>{getProductDisplayName(it.product_name_nl || it.product_name, it.product_name_en)}</td>
                                                    <td>{it.unit_price?.toFixed(2)}</td>
                                                    <td>{it.total?.toFixed(2) ?? '0.00'}</td>
                                                    <td className="text-center">{it.discount_percent ? `${Number(it.discount_percent)}%` : '-'}</td>
                                                    <td>{it.notes || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    <div className="mt-3 pt-3 border-top">
                                        <h5>{t('createOrder.totalLabel')} <span className="text-primary">{order.total?.toFixed(2)}</span></h5>
                                    </div>
                                </>
                            )}

                            {!editing && (
                                <div className="mt-4 pt-3 border-top d-flex flex-wrap gap-4 align-items-start">
                                    <div>
                                        <h6 className="mb-2">{t('orderDetail.invoiceHeading')}</h6>
                                        {(order.invoices || []).length > 0 && (
                                            <div className="mb-2 d-flex flex-wrap gap-2 align-items-center order-invoice-docs">
                                                {order.invoices.map((inv) => (
                                                    <Button
                                                        key={inv.id}
                                                        size="sm"
                                                        variant={(viewInvoiceId === inv.id || (!viewInvoiceId && ['invoice', 'marketing'].includes(inv.kind) && !inv.is_legacy)) ? 'primary' : 'outline-secondary'}
                                                        onClick={() => setViewInvoiceId(inv.id)}
                                                    >
                                                        {['credit_note', 'marketing_credit'].includes(inv.kind) ? `${t('invoices.creditNoteBadge')} ` : ''}{inv.invoice_number}
                                                        {inv.is_test && <span className="badge order-flag-badge order-flag-badge--review">{t('invoices.test.badge')}</span>}
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                        <OrderInvoice order={order} invoiceId={viewInvoiceId} />
                                    </div>
                                    <div>
                                        <h6 className="mb-2">{t('shippingLabel.heading')}</h6>
                                        <ShippingLabel order={order} onOrderUpdate={setOrder} />
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
            <DeliveryStatusEmailModal
                show={!!deliveryModal}
                orderNumber={order.order_number}
                targetStatus={deliveryModal?.targetStatus}
                submitting={deliverySubmitting}
                onCancel={() => setDeliveryModal(null)}
                onConfirm={(sendEmail, expectedDeliveryDate, deliveryWindow) => applyDeliveryStatus(deliveryModal.targetStatus, sendEmail, expectedDeliveryDate, deliveryWindow)}
            />
        </div>
    );
}
